import { useMemo, useState, useRef, useCallback } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import type { Meeting } from "../../store/meetings.store"

/* ─── Constants ───────────────────────────────────────────────── */
const MONTH_KEYS = [
  "common.monthJanuary","common.monthFebruary","common.monthMarch","common.monthApril","common.monthMay","common.monthJune",
  "common.monthJuly","common.monthAugust","common.monthSeptember","common.monthOctober","common.monthNovember","common.monthDecember"
]
const DAY_SHORT_KEYS = [
  "common.daySun","common.dayMon","common.dayTue","common.dayWed",
  "common.dayThu","common.dayFri","common.daySat"
]

/* ─── Helpers ─────────────────────────────────────────────────── */
function getCalendarDays(year: number, month: number) {
  const first    = new Date(year, month, 1).getDay()
  const last     = new Date(year, month + 1, 0).getDate()
  const prevLast = new Date(year, month, 0).getDate()
  const days: { day: number; month: "prev" | "cur" | "next"; date: Date }[] = []

  for (let i = first - 1; i >= 0; i--)
    days.push({ day: prevLast - i, month: "prev", date: new Date(year, month - 1, prevLast - i) })
  for (let d = 1; d <= last; d++)
    days.push({ day: d, month: "cur", date: new Date(year, month, d) })
  let nx = 1
  while (days.length < 42)
    days.push({ day: nx++, month: "next", date: new Date(year, month + 1, nx - 1) })

  return days
}

/* ─── CalendarSheet ───────────────────────────────────────────── */
function CalendarSheet({
  year,
  month,
  today,
  eventDays,
  selectedDate,
  onSelectDate,
  t,
}: {
  year: number
  month: number
  today: Date
  eventDays: Set<number>
  selectedDate?: Date
  onSelectDate?: (date: Date) => void
  t: (key: string) => string
}) {
  const days = getCalendarDays(year, month)

  const isToday = (day: number, m: string) =>
    m === "cur" &&
    day === today.getDate() &&
    month === today.getMonth() &&
    year === today.getFullYear()

  const hasEvent = (day: number, m: string) =>
    m === "cur" && eventDays.has(day)

  const isSelected = (day: number, m: string) => {
    if (!selectedDate) return false
    return (
      m === "cur" &&
      day === selectedDate.getDate() &&
      month === selectedDate.getMonth() &&
      year === selectedDate.getFullYear()
    )
  }

  return (
    // ✅ flex flex-col so inner content can stretch to fill height
    <div className="absolute inset-0 w-full h-full bg-card flex flex-col">
      <div className="relative z-10 px-3 sm:px-5 pt-2 sm:pt-3 pb-2 flex flex-col h-full">

        {/* Day headers — flex-shrink-0 so they never compress */}
        <div className="grid grid-cols-7 gap-0.5 mb-1 flex-shrink-0">
          {DAY_SHORT_KEYS.map(d => (
            <div
              key={d}
              className="text-center py-1 text-[9px] sm:text-[10px] font-semibold tracking-[0.06em] uppercase text-slate-400 dark:text-foreground"
            >
              {t(d)}
            </div>
          ))}
        </div>

        {/*
          ✅ FIX: flex-1 + min-h-0 lets the grid expand to fill all
          remaining height. grid-rows-6 with 1fr rows makes every row
          take an equal share of the available space — no fixed heights,
          no overflow, no clipping on small screens.
        */}
        <div
          className="grid grid-cols-7 flex-1 min-h-0"
          style={{ gridTemplateRows: "repeat(6, 1fr)" }}
        >
          {days.map((d, i) => {
            const todayFlag = isToday(d.day, d.month)
            const eventFlag = hasEvent(d.day, d.month)
            const selectedFlag = isSelected(d.day, d.month)
            const isCur     = d.month === "cur"

            return (
              <div key={i} className="flex items-center justify-center">
                {isCur ? (
                  <button
                    onClick={() => onSelectDate?.(d.date)}
                    /*
                      ✅ FIX: fluid sizing — grows with viewport but is
                      capped at 38px and never shrinks below 26px.
                      aspect-square keeps it circular / square at any size.
                    */
                    className={`
                      relative aspect-square
                      w-[clamp(26px,5.5vw,38px)]
                      rounded-lg sm:rounded-xl
                      flex items-center justify-center
                      text-[11px] sm:text-[13px] font-medium
                      transition-colors
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40
                      ${todayFlag
                        ? "bg-primary text-white font-semibold shadow-[0_4px_12px_rgba(79,70,229,0.35)]"
                        : selectedFlag
                          ? "bg-primary/10 text-primary font-semibold dark:bg-primary/20"
                          : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/70"
                      }
                    `}
                  >
                    {d.day}
                    {eventFlag && (
                      <span
                        className={`
                          absolute bottom-1 left-1/2 -translate-x-1/2
                          w-1 h-1 rounded-full
                          ${todayFlag ? "bg-card/70" : "bg-primary dark:bg-primary/80"}
                        `}
                      />
                    )}
                  </button>
                ) : (
                  // ✅ FIX: placeholder uses identical sizing to keep grid aligned
                  <div className="aspect-square w-[clamp(26px,5.5vw,38px)]" />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ─── Main Component ──────────────────────────────────────────── */
export default function ModernCalendar({
  meetings,
  selectedDate,
  onSelectDate,
}: {
  meetings?: Meeting[]
  selectedDate?: Date
  onSelectDate?: (date: Date) => void
}) {
  const t = useTranslation()
  const NOW = new Date()

  const [year,  setYear]  = useState(NOW.getFullYear())
  const [month, setMonth] = useState(NOW.getMonth())

  const [prevState, setPrevState] = useState({ year: NOW.getFullYear(), month: NOW.getMonth() })

  const [flipDir,    setFlipDir]    = useState<"next" | "prev" | null>(null)
  const [isFlipping, setIsFlipping] = useState(false)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const eventDays = useMemo(() => {
    const set = new Set<number>()
    const list = meetings ?? []
    list.forEach((m) => {
      const d = new Date(m.startTimeMs)
      if (d.getFullYear() === year && d.getMonth() === month) set.add(d.getDate())
    })
    return set
  }, [meetings, month, year])
  const today     = NOW

  /* ── navigate ── */
  const navigate = useCallback(
    (dir: "prev" | "next") => {
      if (isFlipping) return

      setPrevState({ year, month })
      setFlipDir(dir)
      setIsFlipping(true)

      let ny = year, nm = month
      if (dir === "next") { nm++; if (nm > 11) { nm = 0; ny++ } }
      else                { nm--; if (nm < 0)  { nm = 11; ny-- } }

      setYear(ny)
      setMonth(nm)

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        setIsFlipping(false)
        setFlipDir(null)
      }, 680)
    },
    [isFlipping, year, month]
  )

  /* ── goToday ──
   * Jump directly to today's month/year. Skips the page-fold
   * animation entirely — the previous implementation called
   * `navigate("next"/"prev")` which steps a single month at a time,
   * so jumping from July back to May took multiple Today clicks.
   * Today should always be one click, no animation, no in-between
   * months.
   */
  const goToday = useCallback(() => {
    const tm = NOW.getMonth()
    const ty = NOW.getFullYear()
    // Cancel any in-flight flip so the snap to today doesn't fight
    // with the leftover animation timer.
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setIsFlipping(false)
    setFlipDir(null)
    if (month !== tm || year !== ty) {
      setYear(ty)
      setMonth(tm)
      setPrevState({ year: ty, month: tm })
    }
    onSelectDate?.(NOW)
  }, [month, year, onSelectDate, NOW])

  return (
    /*
      ✅ FIX: flex flex-col on the outer wrapper so the header and stage
      participate in the same flex column. h-full means this component
      fills whatever height its parent gives it.
    */
    <div className="w-full rounded-2xl overflow-hidden flex flex-col h-full bg-card dark:bg-card">
      <style>{`
        @keyframes fold-up {
          0%   { transform: perspective(1000px) rotateX(0deg)   translateY(0px)   scaleX(1);    opacity: 1;   filter: brightness(1);    }
          30%  { transform: perspective(1000px) rotateX(-28deg) translateY(-16px) scaleX(0.97); opacity: 1;   filter: brightness(0.88); }
          60%  { transform: perspective(1000px) rotateX(-62deg) translateY(-38px) scaleX(0.93); opacity: 0.7; filter: brightness(0.70); }
          100% { transform: perspective(1000px) rotateX(-90deg) translateY(-58px) scaleX(0.88); opacity: 0;   filter: brightness(0.45); }
        }

        @keyframes fold-down {
          0%   { transform: perspective(1000px) rotateX(0deg)  translateY(0px)   scaleX(1);    opacity: 1;   filter: brightness(1);    }
          30%  { transform: perspective(1000px) rotateX(28deg) translateY(16px)  scaleX(0.97); opacity: 1;   filter: brightness(0.88); }
          60%  { transform: perspective(1000px) rotateX(62deg) translateY(38px)  scaleX(0.93); opacity: 0.7; filter: brightness(0.70); }
          100% { transform: perspective(1000px) rotateX(90deg) translateY(58px)  scaleX(0.88); opacity: 0;   filter: brightness(0.45); }
        }

        @keyframes fade-settle {
          0%   { opacity: 0;   transform: scale(0.97) translateY(8px);  }
          55%  { opacity: 0.9; transform: scale(1.003) translateY(-1px); }
          100% { opacity: 1;   transform: scale(1) translateY(0);        }
        }

        @keyframes shadow-pulse {
          0%   { opacity: 0;   }
          30%  { opacity: 0.5; }
          60%  { opacity: 0.7; }
          100% { opacity: 0;   }
        }

        .fold-up {
          animation: fold-up 460ms cubic-bezier(0.4, 0, 0.7, 0.25) forwards;
          transform-origin: top center;
        }

        .fold-down {
          animation: fold-down 460ms cubic-bezier(0.4, 0, 0.7, 0.25) forwards;
          transform-origin: top center;
        }

        .fade-in-new {
          animation: fade-settle 430ms cubic-bezier(0.22, 1, 0.36, 1) 180ms forwards;
        }

        .shadow-pulse {
          animation: shadow-pulse 500ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
      `}</style>

      {/* ── Header — flex-shrink-0 so it never compresses ── */}
      <div className="flex-shrink-0 flex flex-wrap items-center bg-card justify-between gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4">
        {/* Nav group */}
        <div className="flex items-center">
          {/* Left = Previous month */}
          <button
            onClick={() => navigate("prev")}
            disabled={isFlipping}
            aria-label={t("common.previousMonth")}
            className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-black dark:text-foreground flex items-center justify-center transition-all disabled:cursor-not-allowed disabled:opacity-40 hover:bg-card/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <ChevronDown size={14} className="sm:w-4 sm:h-4" />
          </button>

          {/* Month / Year label */}
          <div className="min-w-[120px] sm:min-w-[140px] text-center text-xs sm:text-sm font-semibold tracking-wide text-primary dark:text-foreground drop-shadow">
            {t(MONTH_KEYS[month])} {year}
          </div>

          {/* Right = Next month */}
          <button
            onClick={() => navigate("next")}
            disabled={isFlipping}
            aria-label={t("common.nextMonth")}
            className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-black dark:text-foreground flex items-center justify-center transition-all disabled:cursor-not-allowed disabled:opacity-40 hover:bg-card/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <ChevronUp size={14} className="sm:w-4 sm:h-4" />
          </button>
        </div>

        {/* Today button — always enabled. goToday cancels any in-flight
            flip and snaps directly to today's month/year, no animation. */}
        <Button
          onClick={goToday}
          variant="ghost"
          size="sm"
          className="text-primary dark:text-foreground text-xs sm:text-sm px-2 sm:px-4 py-1 sm:py-2"
        >
          {t("common.today")}
        </Button>
      </div>

      {/*
        ✅ FIX: flex-1 + min-h-0 lets the stage expand to fill all
        remaining height after the header. Without min-h-0, flexbox
        won't allow shrinking below intrinsic content size → clipping.
      */}
      <div className="relative flex-1 min-h-0 bg-card dark:bg-card/60 overflow-hidden">

        {/* ── NEW page — sits below, fades in as old folds away ── */}
        <div
          className={isFlipping ? "fade-in-new" : ""}
          style={{
            position: "absolute", inset: 0,
            borderRadius: "0 0 14px 14px",
            zIndex: 1,
            opacity: isFlipping ? 0 : 1,
          }}
        >
          <CalendarSheet
            year={year}
            month={month}
            today={today}
            eventDays={eventDays}
            selectedDate={selectedDate}
            onSelectDate={onSelectDate}
            t={t}
          />
        </div>

        {/* ── OLD page — on top, folds away on flip ── */}
        {isFlipping && (
          <div
            className={flipDir === "next" ? "fold-down" : "fold-up"}
            style={{
              position: "absolute", inset: 0,
              borderRadius: "0 0 14px 14px",
              zIndex: 3,
            }}
          >
            <CalendarSheet
              year={prevState.year}
              month={prevState.month}
              today={today}
              eventDays={eventDays}
              selectedDate={selectedDate}
              onSelectDate={onSelectDate}
              t={t}
            />
          </div>
        )}

        {/* ── Page-turn shadow overlay ── */}
        {isFlipping && (
          <div
            className="shadow-pulse"
            style={{
              position: "absolute", inset: 0,
              pointerEvents: "none",
              zIndex: 4,
              background:
                flipDir === "next"
                  ? "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, transparent 45%)"
                  : "linear-gradient(0deg,   rgba(0,0,0,0.10) 0%, transparent 45%)",
            }}
          />
        )}
      </div>
    </div>
  )
}